import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Check, Settings, X, Upload, Camera, Globe, ShoppingCart, Clock, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

function getTimeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function AcceptanceTimer({ expiresAt }) {
  const [timeLeft, setTimeLeft] = useState(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!expiresAt) return;
    const expiry = new Date(expiresAt).getTime();

    const tick = () => {
      const now = Date.now();
      const diff = expiry - now;
      if (diff <= 0) {
        setTimeLeft(0);
        if (intervalRef.current) clearInterval(intervalRef.current);
        return;
      }
      setTimeLeft(diff);
    };

    tick();
    intervalRef.current = setInterval(tick, 1000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [expiresAt]);

  if (timeLeft === null || timeLeft <= 0) return null;

  const mins = Math.floor(timeLeft / 60000);
  const secs = Math.floor((timeLeft % 60000) / 1000);
  const isUrgent = timeLeft < 2 * 60 * 1000; // < 2 min

  return (
    <div className={`flex items-center gap-1.5 mt-2 ${isUrgent ? 'animate-pulse' : ''}`}>
      <Clock className="w-3.5 h-3.5" strokeWidth={2.5} style={{ color: isUrgent ? '#FF6B6B' : '#E8C86A' }} />
      <span
        className="text-xs font-mono font-bold"
        style={{ fontFamily: 'JetBrains Mono, monospace', color: isUrgent ? '#FF6B6B' : '#888' }}
      >
        {String(mins).padStart(2, '0')}:{String(secs).padStart(2, '0')} to pay
      </span>
    </div>
  );
}

function UrgentPaymentBanner({ acceptances, onCheckout }) {
  const [now, setNow] = useState(Date.now());

  // Find pending acceptances with active expiry
  const pendingWithExpiry = acceptances.filter(
    a => a.status === 'pending' && a.expires_at
  );

  useEffect(() => {
    if (pendingWithExpiry.length === 0) return;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [pendingWithExpiry.length]);

  if (pendingWithExpiry.length === 0) return null;

  // Find the soonest expiring one
  const soonest = pendingWithExpiry.reduce((min, a) => {
    const exp = new Date(a.expires_at).getTime();
    return exp < min.exp ? { ...a, exp } : min;
  }, { exp: Infinity });

  const diff = soonest.exp - now;
  if (diff <= 0) return null;

  const mins = Math.floor(diff / 60000);
  const secs = Math.floor((diff % 60000) / 1000);
  const isUrgent = diff < 2 * 60 * 1000;
  const unpaidCount = acceptances.filter(a => a.status === 'pending').length;
  const unpaidTotal = acceptances
    .filter(a => a.status === 'pending')
    .reduce((sum, a) => sum + (a.asking_price || 0), 0);

  return (
    <div
      className={`border-[3px] border-[#121212] shadow-[6px_6px_0px_0px_#121212] rounded-2xl p-5 md:p-6 mb-8 ${isUrgent ? 'animate-pulse' : ''}`}
      style={{ backgroundColor: isUrgent ? '#FF6B6B' : '#FEF3C7' }}
      data-testid="urgent-payment-banner"
    >
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div
            className="w-14 h-14 rounded-xl border-[3px] border-[#121212] flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: isUrgent ? '#121212' : '#FF6B6B' }}
          >
            <AlertTriangle className="w-7 h-7" strokeWidth={2.5} style={{ color: isUrgent ? '#FF6B6B' : '#121212' }} />
          </div>
          <div>
            <div className="font-black uppercase text-base md:text-lg tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {unpaidCount} unpaid acceptance{unpaidCount > 1 ? 's' : ''} · ${unpaidTotal}
            </div>
            <div className="flex items-center gap-2 mt-1">
              <Clock className="w-4 h-4" strokeWidth={2.5} style={{ color: isUrgent ? '#121212' : '#FF6B6B' }} />
              <span
                className="font-mono text-2xl md:text-3xl font-black"
                style={{ fontFamily: 'JetBrains Mono, monospace', color: isUrgent ? '#121212' : '#FF6B6B' }}
              >
                {String(mins).padStart(2, '0')}:{String(secs).padStart(2, '0')}
              </span>
              <span className="text-sm font-bold" style={{ color: '#555' }}>before auto-release</span>
            </div>
          </div>
        </div>
        <button
          onClick={onCheckout}
          className="bg-[#121212] text-[#F6F1E8] font-black uppercase text-sm border-[3px] border-[#121212] shadow-[3px_3px_0px_0px_rgba(0,0,0,0.3)] hover:-translate-y-[2px] hover:shadow-[5px_5px_0px_0px_rgba(0,0,0,0.3)] active:translate-y-[3px] active:shadow-none transition-all rounded-xl px-8 py-3.5 flex items-center gap-2 flex-shrink-0"
          data-testid="urgent-checkout-btn"
        >
          <ShoppingCart className="w-4 h-4" strokeWidth={3} />
          Pay Now
        </button>
      </div>
    </div>
  );
}

export default function StartupDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showProfile, setShowProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({ startup_name: '', tagline: '', website: '', description: '' });
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = React.useRef(null);

  // My acceptances
  const [acceptances, setAcceptances] = useState([]);
  const [marketOpen, setMarketOpen] = useState(true);
  const [bidStats, setBidStats] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [profileRes, acceptRes, settingsRes, statsRes] = await Promise.all([
        axios.get(`${API}/users/me/profile`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/startup/acceptances`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/settings`).catch(() => ({ data: {} })),
        axios.get(`${API}/wearer-bids/stats`).catch(() => ({ data: null })),
      ]);
      setProfile(profileRes.data);
      setAcceptances(acceptRes.data || []);
      setMarketOpen(settingsRes.data?.market_open !== false);
      setBidStats(statsRes.data);
      if (profileRes.data) {
        setProfileForm({
          startup_name: profileRes.data.startup_name || '',
          tagline: profileRes.data.tagline || '',
          website: profileRes.data.website || '',
          description: profileRes.data.description || '',
        });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);



  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { alert('Please select an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { alert('Image must be under 5MB'); return; }
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.put(`${API}/startup/logo`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      await loadData();
    } catch (err) { alert(err.response?.data?.detail || 'Logo upload failed'); }
    finally { setUploadingLogo(false); }
  };

  const handleProfileUpdate = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/users/me/profile`, profileForm, { withCredentials: true });
      await loadData();
      setShowProfile(false);
    } catch (err) { alert(err.response?.data?.detail || 'Update failed'); }
    finally { setSaving(false); }
  };

  const getLogoUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return `${BACKEND}/api/files/${path}`;
  };



  const inputClass = "w-full bg-white border-[3px] border-[#121212] shadow-[3px_3px_0px_0px_#121212] rounded-xl p-3 font-medium text-sm focus:outline-none focus:-translate-y-[1px] focus:shadow-[4px_4px_0px_0px_#121212] transition-all";

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F6F1E8' }}>
        <div className="animate-spin w-8 h-8 border-4 border-[#121212] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F6F1E8' }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <div className="relative group">
              <div
                className="w-16 h-16 rounded-2xl border-[3px] border-[#121212] shadow-[3px_3px_0px_0px_#121212] flex items-center justify-center overflow-hidden cursor-pointer"
                style={{ backgroundColor: profile?.logo_url ? 'transparent' : '#E8C86A' }}
                onClick={() => logoInputRef.current?.click()}
                data-testid="logo-upload-area"
              >
                {profile?.logo_url ? (
                  <img src={getLogoUrl(profile.logo_url)} alt="Logo" className="w-full h-full object-cover" />
                ) : (
                  <span className="font-black text-2xl" style={{ fontFamily: 'Outfit, sans-serif' }}>{(profile?.startup_name || 'S')[0]}</span>
                )}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-2xl">
                  {uploadingLogo ? <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" /> : <Camera className="w-5 h-5 text-white" strokeWidth={3} />}
                </div>
              </div>
              <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} data-testid="logo-file-input" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight uppercase" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="startup-dashboard-title">
                {profile?.startup_name || 'Startup'}
              </h1>
              {profile?.tagline && <p className="text-sm font-medium mt-0.5" style={{ color: '#666' }}>{profile.tagline}</p>}
              {profile?.website && (
                <a href={profile.website} target="_blank" rel="noopener noreferrer" className="text-xs font-bold flex items-center gap-1 mt-0.5 hover:text-[#FF6B6B]" style={{ color: '#888' }}>
                  <Globe className="w-3 h-3" strokeWidth={2.5} /> {profile.website.replace(/https?:\/\//, '')}
                </a>
              )}
            </div>
          </div>
          <button
            data-testid="edit-profile-btn"
            onClick={() => setShowProfile(true)}
            className="bg-[#EDE3C7] text-[#121212] font-black uppercase text-sm border-[3px] border-[#121212] shadow-[3px_3px_0px_0px_#121212] hover:-translate-y-[1px] hover:shadow-[4px_4px_0px_0px_#121212] active:translate-y-[3px] active:shadow-none transition-all rounded-xl px-4 py-2.5 flex items-center gap-2"
          >
            <Settings className="w-4 h-4" strokeWidth={3} /> Profile
          </button>
        </div>

        {/* Profile Edit Modal */}
        {showProfile && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowProfile(false)}>
            <div className="bg-[#EDE3C7] border-[3px] border-[#121212] shadow-[6px_6px_0px_0px_#121212] rounded-2xl p-6 w-full max-w-lg animate-fade-in-up" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-black uppercase tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Edit Profile</h2>
                <button onClick={() => setShowProfile(false)}><X className="w-5 h-5" strokeWidth={3} /></button>
              </div>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-20 h-20 rounded-2xl border-[3px] border-[#121212] shadow-[3px_3px_0px_0px_#121212] flex items-center justify-center overflow-hidden cursor-pointer relative group" style={{ backgroundColor: profile?.logo_url ? 'transparent' : '#E8C86A' }} onClick={() => logoInputRef.current?.click()}>
                  {profile?.logo_url ? <img src={getLogoUrl(profile.logo_url)} alt="Logo" className="w-full h-full object-cover" /> : <span className="font-black text-3xl">{(profile?.startup_name || 'S')[0]}</span>}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-2xl"><Upload className="w-6 h-6 text-white" strokeWidth={3} /></div>
                </div>
                <div>
                  <p className="font-bold text-sm">Startup Logo</p>
                  <p className="text-xs" style={{ color: '#888' }}>Click to upload. Max 5MB.</p>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-black uppercase tracking-wider mb-2">Startup Name *</label>
                  <input data-testid="profile-name-input" className={inputClass} value={profileForm.startup_name} onChange={e => setProfileForm(p => ({ ...p, startup_name: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-xs font-black uppercase tracking-wider mb-2">Tagline</label>
                  <input className={inputClass} value={profileForm.tagline} onChange={e => setProfileForm(p => ({ ...p, tagline: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-xs font-black uppercase tracking-wider mb-2">Website</label>
                  <input className={inputClass} value={profileForm.website} onChange={e => setProfileForm(p => ({ ...p, website: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-xs font-black uppercase tracking-wider mb-2">Description</label>
                  <textarea className={`${inputClass} min-h-[80px] resize-none`} value={profileForm.description} onChange={e => setProfileForm(p => ({ ...p, description: e.target.value }))} />
                </div>
                <button data-testid="save-profile-btn" onClick={handleProfileUpdate} disabled={saving} className="w-full bg-[#FF6B6B] text-[#121212] font-black uppercase border-[3px] border-[#121212] shadow-[4px_4px_0px_0px_#121212] hover:-translate-y-[2px] hover:shadow-[6px_6px_0px_0px_#121212] active:translate-y-[4px] active:shadow-none transition-all rounded-xl px-6 py-3 disabled:opacity-50">
                  {saving ? 'Saving...' : 'Save Profile'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Urgent Payment Banner */}
        <UrgentPaymentBanner acceptances={acceptances} onCheckout={() => navigate('/dashboard/startup/checkout')} />

        {/* Stats Row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Available Students', value: bidStats?.active_bids || 0, color: '#6EE7B7' },
            { label: 'My Accepted', value: acceptances.length, color: '#E8C86A' },
            { label: 'Median Ask', value: `$${bidStats?.avg_price || 0}`, color: '#FF6B6B' },
            { label: 'Total Active', value: bidStats?.active_bids || 0, color: '#D8DFEA' },
          ].map((s, i) => (
            <div key={i} className="bg-[#EDE3C7] border-[3px] border-[#121212] shadow-[3px_3px_0px_0px_#121212] rounded-xl p-4">
              <div className="font-mono text-2xl font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{s.value}</div>
              <div className="text-xs font-black uppercase tracking-wider" style={{ color: '#888' }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Market Napping Banner */}
        {!marketOpen && (
          <div className="bg-[#121212] border-[3px] border-[#E8C86A] shadow-[6px_6px_0px_0px_#E8C86A] rounded-2xl p-8 md:p-12 text-center mb-8 relative overflow-hidden" data-testid="market-napping-card">
            <div className="nap-glow" />
            <div className="flex items-end justify-center gap-1 mb-5 h-16">
              <span className="nap-z">z</span>
              <span className="nap-z">z</span>
              <span className="nap-z">Z</span>
            </div>
            <h2 className="text-xl md:text-2xl font-black uppercase tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif', color: '#F6F1E8' }}>The Market is Napping</h2>
            <p className="text-sm md:text-base font-medium mb-1" style={{ color: '#999' }}>The Market opens on 04/15 at 10:00 A.M PST, so that everyone gets the most competitive price.</p>
            <p className="text-sm md:text-base font-medium mb-6" style={{ color: '#999' }}>Grab a coffee. We'll be back before it gets cold.</p>
            <div className="inline-block bg-[#EDE3C7] text-[#121212] font-black uppercase text-sm border-[3px] border-[#121212] rounded-xl px-6 py-3 cursor-default" data-testid="check-back-btn">
              Check Back Soon
            </div>
          </div>
        )}

        {/* My Accepted Wearers */}
        {acceptances.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-black uppercase tracking-tight mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Check className="w-5 h-5 inline mr-2 -mt-0.5" strokeWidth={2.5} /> My Accepted Students
            </h2>
            <div className="space-y-3">
              {acceptances.filter(a => a.status !== 'expired').map(a => (
                <div key={a.acceptance_id} className={`border-[3px] border-[#121212] shadow-[3px_3px_0px_0px_#121212] rounded-2xl p-4 flex items-center justify-between ${a.status === 'confirmed' ? 'bg-[#6EE7B7]/20' : 'bg-[#FF6B6B]/20'}`} data-testid={`acceptance-${a.acceptance_id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl border-[2px] border-[#121212] flex items-center justify-center font-black text-sm" style={{ backgroundColor: a.avatar_color || '#E8C86A' }}>
                      {(a.wearer_name || 'M')[0]}
                    </div>
                    <div>
                      <div className="font-bold text-sm">{a.wearer_name || 'Mystery Student'}</div>
                      <div className="text-xs font-medium" style={{ color: '#888' }}>{a.garment_type} · {getTimeAgo(a.created_at)}</div>
                      {a.status === 'pending' && a.expires_at && <AcceptanceTimer expiresAt={a.expires_at} />}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-lg font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>${a.asking_price}</div>
                    <span className={`text-xs font-black uppercase ${a.status === 'confirmed' ? 'text-[#16a34a]' : 'text-[#E8C86A]'}`}>{a.status === 'confirmed' ? 'Paid ✓' : 'Pending'}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Checkout CTA — only show if there are unpaid acceptances */}
            {acceptances.some(a => a.status !== 'confirmed') && (
              <button
                onClick={() => navigate('/dashboard/startup/checkout')}
                className="w-full sm:w-auto bg-[#FF6B6B] text-[#121212] font-black uppercase text-sm border-[3px] border-[#121212] shadow-[4px_4px_0px_0px_#121212] hover:-translate-y-[2px] hover:shadow-[6px_6px_0px_0px_#121212] active:translate-y-[4px] active:shadow-none transition-all rounded-xl px-6 py-3 flex items-center justify-center gap-2 mt-4"
                data-testid="checkout-cta-btn"
              >
                <ShoppingCart className="w-4 h-4" strokeWidth={3} />
                Proceed to Checkout ({acceptances.filter(a => a.status !== 'confirmed').length} unpaid)
              </button>
            )}

            {/* Send Graphic CTA — show when all acceptances are confirmed */}
            {acceptances.length > 0 && acceptances.every(a => a.status === 'confirmed') && (
              <div
                className="bg-[#D8DFEA] border-[3px] border-[#121212] shadow-[4px_4px_0px_0px_#121212] rounded-2xl p-6 mt-4 text-center"
                data-testid="send-graphic-cta"
              >
                <div className="text-3xl mb-3">📧</div>
                <h3 className="text-lg font-black uppercase tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Send Us Your Graphic
                </h3>
                <p className="text-sm font-medium mb-3" style={{ color: '#555' }}>
                  All payments confirmed! Email us your graphic at:
                </p>
                <div
                  className="inline-block bg-[#121212] text-[#F6F1E8] font-black uppercase text-sm border-[3px] border-[#121212] shadow-[3px_3px_0px_0px_#666] rounded-xl px-6 py-3"
                  data-testid="email-graphic-link"
                >
                  support@surfacearea.shop
                </div>
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}
